import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useSwipeToClose from "../../../hooks/useSwipeToClose";
import { GROCERY_HOST } from "../../../services/apiConfig";
import { getAuthHeaders } from "../utils/productListUtils";

const SizeAdjustDrawer = ({
  isOpen,
  productId,
  productName,
  currentSizes,
  onSizeUpdated,
  closeDrawer,
}) => {
  const {
    containerRef,
    dragOffset,
    isAnimating,
    shouldRender,
    isVisible,
    isDragging,
    DragHandle,
  } = useSwipeToClose(closeDrawer, isOpen);
  const portalContainerRef = useRef(null);
  const [sizesJson, setSizesJson] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const container = document.createElement("div");
    container.id = "size-adjust-portal";
    container.style.position = "relative";
    container.style.zIndex = "99999";
    document.body.appendChild(container);
    portalContainerRef.current = container;

    return () => {
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError("");
      setSuccess("");
      if (currentSizes) {
        try {
          setSizesJson(JSON.stringify(currentSizes, null, 2));
        } catch {
          setSizesJson("");
        }
      }
    }
  }, [isOpen, currentSizes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sizesJson.trim() || !productId) return;

    setError("");
    setSuccess("");

    let parsedSizes;
    try {
      parsedSizes = JSON.parse(sizesJson);
    } catch (err) {
      setError("Invalid JSON: " + err.message);
      return;
    }

    if (!Array.isArray(parsedSizes)) {
      setError("Sizes must be a JSON array");
      return;
    }

    if (parsedSizes.length === 0) {
      setError("Sizes array must contain at least one size object");
      return;
    }

    for (let index = 0; index < parsedSizes.length; index += 1) {
      if (typeof parsedSizes[index] !== "object" || parsedSizes[index] === null) {
        setError(`Item at index ${index} is not a valid object`);
        return;
      }
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${GROCERY_HOST}/api/products/${encodeURIComponent(productId)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ sizes: parsedSizes }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      const overrideResponse = await fetch(`${GROCERY_HOST}/api/size-overrides`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          id: `${productId}_override_${Date.now()}`,
          slug: productId,
          size: JSON.stringify(parsedSizes),
        }),
      });

      if (!overrideResponse.ok) {
        console.warn(
          "Failed to write to size_overrides collection:",
          await overrideResponse.text()
        );
      }

      setSuccess("Sizes updated.");
      if (onSizeUpdated) {
        onSizeUpdated({ productId, newSizes: parsedSizes });
      }
      setTimeout(() => closeDrawer(), 1200);
    } catch (err) {
      setError(err.message || "Failed to update sizes");
    } finally {
      setIsLoading(false);
    }
  };

  if (!shouldRender || !portalContainerRef.current) return null;

  return createPortal(
    <div
      className="fixed flex items-end justify-center inset-0 bg-[#0D0C07] drawer-overlay"
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: "background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        zIndex: 99999,
      }}
      onClick={closeDrawer}
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[85dvh]"
        style={{
          transform: isVisible
            ? `translate3d(0, ${Math.max(0, dragOffset)}px, 0)`
            : "translate3d(0, 100%, 0)",
          transition:
            (dragOffset === 0 && isAnimating) ||
            (!isDragging && dragOffset === 0)
              ? "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />
        <button
          onClick={closeDrawer}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z"
              fill="#56554D"
            />
          </svg>
        </button>

        <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-6 pt-8">
          <h2 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18px] font-bold leading-[28px] mb-2">
            Adjust product sizes
          </h2>
          <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] mb-4">
            Edit the sizes array for "<strong>{productName}</strong>"
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="sizes-input"
                className="block text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-medium mb-2"
              >
                Sizes JSON *
              </label>
              <textarea
                id="sizes-input"
                value={sizesJson}
                onChange={(e) => setSizesJson(e.target.value)}
                placeholder='[{"name": "Size 1", "price": 1.99, ...}]'
                className="w-full px-4 py-3 border border-[var(--Color-Grays-Gray-3,#E8E6E3)] rounded-[8px] text-[12px] font-mono focus:outline-none focus:border-[#FFCB00] focus:ring-1 focus:ring-[#FFCB00] min-h-[300px] resize-y"
                disabled={isLoading}
                spellCheck={false}
              />
            </div>

            {error && (
              <div className="bg-[#FEE2E2] text-[#991B1B] px-4 py-3 rounded-[8px] text-[14px]">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-[#DCFCE7] text-[#166534] px-4 py-3 rounded-[8px] text-[14px]">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !sizesJson.trim()}
              className={`w-full py-3 px-4 rounded-[8px] text-[14px] font-semibold transition-colors ${
                isLoading || !sizesJson.trim()
                  ? "bg-[var(--Color-Grays-Gray-3,#E8E6E3)] text-[var(--Color-Grays-Gray-6,#A09F9C)] cursor-not-allowed"
                  : "bg-[#FFCB00] text-[#0D0C07] hover:bg-[#E6B800]"
              }`}
            >
              {isLoading ? "Updating..." : "Update sizes"}
            </button>
          </form>
        </div>
      </div>
    </div>,
    portalContainerRef.current
  );
};

export default SizeAdjustDrawer;
